import { Loader2, FileImage } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";
import { SpreadsheetViewer } from "@/components/common/spreadsheet-viewer";

// ── File-type helpers ─────────────────────────────────────────────────────────

/**
 * Extract the lowercased extension from a URL or filename. For URLs, isolates
 * the pathname first so host dots (e.g. `example.com`) don't leak into the
 * detected extension. Strips query string and hash so signed URLs
 * (e.g. `.xlsx?X-Amz-…`) don't leak signature text into the detection.
 *
 * Returns "" when the input has no extension.
 */
export const getFileType = (source: string): string => {
  if (!source) return "";
  // Prefer parsing as a URL so host segments (example.com) don't pollute the
  // extension we extract. Fall back to raw string for bare filenames.
  let path = source;
  try {
    path = new URL(source).pathname;
  } catch {
    // Not a parseable URL — treat as a filename or relative path.
    path = source.split("?")[0].split("#")[0];
  }
  const filename = path.split("/").pop() ?? "";
  const dot = filename.lastIndexOf(".");
  return dot === -1 ? "" : filename.slice(dot + 1).toLowerCase();
};

export const isPDF = (type: string) => type.toLowerCase() === "pdf";
export const isPowerPoint = (type: string) => {
  const t = type.toLowerCase();
  return t === "ppt" || t === "pptx";
};
export const isWordDocument = (type: string) => {
  const t = type.toLowerCase();
  return t === "doc" || t === "docx";
};
export const isExcelFile = (type: string) => {
  const t = type.toLowerCase();
  return t === "xlsx" || t === "xls";
};
export const isCSVFile = (type: string) => type.toLowerCase() === "csv";
export const isImage = (type: string) => {
  const t = type.toLowerCase();
  return t === "jpg" || t === "jpeg" || t === "png" || t === "ico";
};

/**
 * Only accept http(s) URLs for embedding. Rejects `javascript:`, `data:`,
 * `blob:`, `filesystem:`, `about:`, etc. — any of which could be used to
 * break out of the iframe or execute arbitrary code in the app's context.
 */
export const isSafeHttpUrl = (url: string): boolean => {
  try {
    const { protocol } = new URL(url);
    return protocol === "https:" || protocol === "http:";
  } catch {
    return false;
  }
};

// ── Internal: iframe with loader ─────────────────────────────────────────────

/**
 * Shared loader-overlay + iframe renderer used by PDF/Word/PowerPoint branches.
 * `referrerPolicy="no-referrer"` prevents leaking the signed URL via the
 * Referer header when the external viewer loads sub-resources.
 */
function IframeViewer({ src, title }: { src: string; title: string }) {
  return (
    <>
      <div className="absolute inset-0 flex items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
      <iframe
        src={src}
        className="w-full h-full"
        title={title}
        referrerPolicy="no-referrer"
        onLoad={(e) => {
          const iframe = e.target as HTMLIFrameElement;
          const loader = iframe.previousSibling as HTMLDivElement;
          if (loader) loader.style.display = "none";
        }}
      />
    </>
  );
}

// ── Viewer component ─────────────────────────────────────────────────────────

interface FilePreviewDialogProps {
  url: string;
  fileName: string;
  /** Optional — BE proxy id used by SpreadsheetViewer for CORS-safe fetch. */
  fileId?: string;
  /** Trigger slot — e.g. an icon button. If omitted, caller controls `open`. */
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Override the extension detection (for cases where URL has no extension). */
  typeOverride?: string;
}

/**
 * Multi-format file previewer matching the Data Room / Portal preview UX.
 * Accepts a trigger via `children` (shadcn Dialog pattern) or external
 * controlled open state.
 */
export function FilePreviewDialog({
  url,
  fileName,
  fileId,
  children,
  open,
  onOpenChange,
  typeOverride,
}: FilePreviewDialogProps) {
  const fileType = typeOverride ?? getFileType(fileName || url);

  const content = (
    <DialogContent className="max-w-4xl h-[80vh] overflow-hidden">
      <div className="relative w-full h-full overflow-hidden">
        {isPDF(fileType) ? (
          <IframeViewer
            src={`https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`}
            title={fileName}
          />
        ) : isPowerPoint(fileType) ? (
          <IframeViewer
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`}
            title={fileName}
          />
        ) : isWordDocument(fileType) ? (
          <IframeViewer
            src={`https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`}
            title={fileName}
          />
        ) : isCSVFile(fileType) || isExcelFile(fileType) ? (
          <SpreadsheetViewer url={url} fileName={fileName} fileId={fileId} />
        ) : isImage(fileType) ? (
          <div className="relative flex items-center justify-center w-full h-full p-4 overflow-hidden">
            <img
              src={url}
              alt={fileName}
              className="w-full h-full object-contain shadow-sm rounded-md"
              onError={(e) => {
                const target = e.currentTarget;
                target.style.display = "none";
                const fallback = target.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = "flex";
              }}
            />
            <div
              className="flex-col items-center justify-center gap-2 text-gray-400"
              style={{ display: "none" }}
            >
              <FileImage className="w-16 h-16" />
              <p className="text-sm">Image preview not available</p>
            </div>
          </div>
        ) : isSafeHttpUrl(url) ? (
          // Unknown extension: render inside a sandboxed iframe with a strict
          // referrer policy. `allow-same-origin` is intentionally omitted so
          // the embedded content cannot access cookies / localStorage.
          <iframe
            src={url}
            className="w-full h-full"
            title={fileName}
            sandbox="allow-scripts allow-popups allow-forms"
            referrerPolicy="no-referrer"
          />
        ) : (
          // Non-http(s) URL — never embed and never render a clickable link
          // for it, since the scheme (e.g. `javascript:`, `blob:`, `data:`)
          // could execute in the user's context or leak the token.
          <div className="flex flex-col items-center justify-center w-full h-full gap-3 p-6 text-center">
            <FileImage className="w-12 h-12 text-muted-foreground/60" />
            <p className="text-sm font-medium text-foreground">{fileName}</p>
            <p className="text-sm text-muted-foreground">
              This file can't be previewed or opened from here.
            </p>
          </div>
        )}
      </div>
    </DialogContent>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      {content}
    </Dialog>
  );
}
